/**
 * SVG path builders for chart marks.
 *
 * Both follow the same mark spec: a 4px rounded *data end* and a square
 * baseline, so the bar visibly grows from its axis rather than floating as a
 * fully rounded lozenge.
 */

/** Column growing upward from `baselineY` to `topY`. */
export function columnPath(
  x: number,
  topY: number,
  width: number,
  baselineY: number,
  radius = 4,
): string {
  const height = Math.max(baselineY - topY, 0)
  if (height <= 0.5) return ''
  const r = Math.min(radius, width / 2, height)
  return [
    `M ${x} ${baselineY}`,
    `L ${x} ${topY + r}`,
    `Q ${x} ${topY} ${x + r} ${topY}`,
    `L ${x + width - r} ${topY}`,
    `Q ${x + width} ${topY} ${x + width} ${topY + r}`,
    `L ${x + width} ${baselineY}`,
    'Z',
  ].join(' ')
}

/** Bar growing rightward from `baselineX` to `endX`. */
export function barPath(
  baselineX: number,
  y: number,
  endX: number,
  height: number,
  radius = 4,
): string {
  const width = Math.max(endX - baselineX, 0)
  if (width <= 0.5) return ''
  const r = Math.min(radius, height / 2, width)
  return [
    `M ${baselineX} ${y}`,
    `L ${baselineX + width - r} ${y}`,
    `Q ${baselineX + width} ${y} ${baselineX + width} ${y + r}`,
    `L ${baselineX + width} ${y + height - r}`,
    `Q ${baselineX + width} ${y + height} ${baselineX + width - r} ${y + height}`,
    `L ${baselineX} ${y + height}`,
    'Z',
  ].join(' ')
}

/** Axis ticks rounded to readable numbers. */
export function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0]
  const rawStep = max / count
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalised = rawStep / magnitude
  const step =
    (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude

  const ticks: number[] = []
  for (let value = 0; value <= max + step * 0.001; value += step) {
    ticks.push(Math.round(value))
  }
  return ticks
}
